import * as layout from '../../components/markLayout.module.css';
import { MarkButton } from '../../components/MarkControls';
import React, { useCallback } from 'react';
import { useSearchParams, useNavigate, Outlet } from 'react-router-dom';
import Section from '@hero/ui-kit/layout/Section';
import { MarkPageHeader, MarkEmptyState } from '../../components/MarkLayout';

import usePagerProps from '../../../../hooks/usePagerProps';
import useBlasts from '../../api/useBlasts';
import BlastsList from './BlastsList';
import LimitItems from '../../../../components/LimitItems';
import InlineLoader from '../../../../components/InlineLoader';
import QueryInlineError from '../../../../components/QueryInlineError';
import type { BlastStatus } from '../../types';
const STATUS_CHIPS: {
    value: BlastStatus | '';
    label: string;
}[] = [
    { value: '', label: 'All' },
    { value: 'DRAFT', label: 'Draft' },
    { value: 'SCHEDULED', label: 'Scheduled' },
    { value: 'SENDING', label: 'Sending' },
    { value: 'CANCELLING', label: 'Cancelling' },
    { value: 'COMPLETED', label: 'Completed' },
    { value: 'CANCELLED', label: 'Cancelled' },
    { value: 'FAILED', label: 'Failed' }
];
const BlastsPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const offset = Number(searchParams.get('offset') || 0);
    const limit = Number(searchParams.get('limit') || 20);
    const statusFilter = (searchParams.get('status') as BlastStatus) || undefined;
    const {
        data: blastsResponse,
        isFetching,
        isLoading,
        error
    } = useBlasts({
        offset,
        limit,
        status: statusFilter
    });
    const blasts = blastsResponse?.data?.blasts || [];
    const totalCount = blastsResponse?.data?.total_count || 0;
    const handleStatusChange = useCallback(
        (status: BlastStatus | '') => {
            setSearchParams((prev) => {
                if (status) {
                    prev.set('status', status);
                } else {
                    prev.delete('status');
                }
                prev.delete('offset');
                return prev;
            });
        },
        [setSearchParams]
    );
    const pagerProps = usePagerProps({
        total_count: totalCount,
        limit,
        offset
    });
    return (
        <Section subtheme={['regular', 'background']} className={layout.page} noDefaultPadding>
            <MarkPageHeader
                title="Blasts"
                description="Create one-time campaigns, manage their schedule, and review delivery results."
            >
                <MarkButton
                    type="button"
                    size="regular"
                    variant="primary"
                    onClick={() =>
                        navigate({ pathname: '/mark/blasts/new', search: searchParams.toString() })
                    }
                >
                    New blast
                </MarkButton>
            </MarkPageHeader>
            <div
                className={layout.actions}
                style={{ justifyContent: 'flex-start' }}
                role="group"
                aria-label="Filter by status"
            >
                {STATUS_CHIPS.map((chip) => (
                    <MarkButton
                        key={chip.value || 'all'}
                        type="button"
                        onClick={() => handleStatusChange(chip.value)}
                        aria-pressed={(statusFilter || '') === chip.value}
                    >
                        {chip.label}
                    </MarkButton>
                ))}
            </div>
            <LimitItems
                isLoading={isLoading}
                limit={limit}
                offset={offset}
                total_count={totalCount}
            />
            {error ? (
                <QueryInlineError error={error} />
            ) : isLoading ? (
                <InlineLoader />
            ) : blasts.length === 0 ? (
                <MarkEmptyState
                    title={statusFilter ? 'No blasts with this status' : 'No blasts yet'}
                    description={
                        statusFilter
                            ? 'Try another status or clear the filter to see all blasts.'
                            : 'Create your first blast to get started.'
                    }
                >
                    {statusFilter && (
                        <MarkButton onClick={() => handleStatusChange('')}>Clear filter</MarkButton>
                    )}
                </MarkEmptyState>
            ) : (
                <BlastsList blasts={blasts} isLoading={isFetching} pagerProps={pagerProps} />
            )}
            <Outlet />
        </Section>
    );
};
export default BlastsPage;
